import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { formatInTimeZone } from 'date-fns-tz';
import { tr } from 'date-fns/locale';
import { enUS } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import './OrderPage.css';
import FileTypeModal from './FileTypeModal';
import './FileTypeModal.css';
import TimezoneSwitcher from './TimezoneSwitcher';
import './TimezoneSwitcher.css';
import { timezones } from '../data/timezones';
import { useAuth } from '../context/AuthContext';
import AuthModal from './AuthModal';
import PaymentModal from './PaymentModal';
import { rates } from '../data/exchangeRates';
import SuccessModal from './SuccessModal';
const API_URL = process.env.REACT_APP_API_URL;

const customSelectStyles = {
  control: (provided, state) => ({ ...provided, border: '1px solid var(--border-color)', borderRadius: '8px', padding: '8px', boxShadow: state.isFocused ? '0 0 0 2px rgba(52, 152, 219, 0.2)' : 'none', '&:hover': { borderColor: 'var(--accent-color)', }, }),
  valueContainer: (provided) => ({ ...provided, padding: '0', }),
  placeholder: (provided) => ({ ...provided, color: '#bdc3c7', fontWeight: '400', }),
  multiValue: (provided) => ({ ...provided, backgroundColor: '#ecf0f1', borderRadius: '6px', margin: '2px', }),
  multiValueLabel: (provided) => ({ ...provided, color: 'var(--text-color)', fontWeight: '500', padding: '6px', }),
  multiValueRemove: (provided) => ({ ...provided, color: '#95a5a6', borderRadius: '0 6px 6px 0', '&:hover': { backgroundColor: '#e74c3c', color: 'white', }, }),
  dropdownIndicator: (provided) => ({ ...provided, color: '#bdc3c7', '&:hover': { color: 'var(--text-color)', }, }),
  option: (provided, state) => ({ ...provided, backgroundColor: state.isSelected ? 'var(--primary-color)' : state.isFocused ? 'var(--light-gray)' : 'white', color: state.isSelected ? 'white' : 'var(--text-color)', fontWeight: '500', cursor: 'pointer', }),
};

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const OrderPage = ({ selectedCurrency, initialTimezone, initialSourceLang }) => {
  const { t, i18n } = useTranslation();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [activeStep, setActiveStep] = useState(1);
  const [order, setOrder] = useState({ 
    sourceLang: initialSourceLang || 'en', targetLangs: [], wordCount: 0, files: [], text: '', 
    translatorType: 'professional_translator', expertise: 'general', deliverySpeed: 'standard', 
    extraServices: { dtp: false, notary: false, kargo: false }, 
  });
  
  const [totalPrice, setTotalPrice] = useState(0);
  const [basePriceTL, setBasePriceTL] = useState(0);
  const [inputMode, setInputMode] = useState('file');
  const [isCounting, setIsCounting] = useState(false);
  
  const [isFileTypeModalOpen, setIsFileTypeModalOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isTimezoneOpen, setIsTimezoneOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });

  const [selectedTimezone, setSelectedTimezone] = useState(initialTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
  const step2Ref = useRef(null);
  
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  
  const [dbLanguages, setDbLanguages] = useState([]);
  const [pricingConfig, setPricingConfig] = useState(null);

  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
const [modalMessage, setModalMessage] = useState('');
  useEffect(() => {
   const fetchInitialData = async () => {
        try {
            const [langsResponse, configResponse] = await Promise.all([
                fetch(`${API_URL}/api/public/languages`),
                fetch(`${API_URL}/api/public/pricing-config`)
            ]);
            if (!langsResponse.ok || !configResponse.ok) throw new Error('Başlangıç verileri alınamadı.');
            
            const langsData = await langsResponse.json();
            const configData = await configResponse.json();
            setDbLanguages(langsData);
            setPricingConfig(configData);
        } catch (err) {
            console.error("Başlangıç verileri alınamadı:", err);
            setSubmitMessage({ type: 'error', text: t('error_fetching_initial_data') });
        } 
    };
    fetchInitialData();
  }, [t]);

  const languageOptions = useMemo(() => {
    if (!dbLanguages) return [];
    const priorityCodes = ['tr', 'en', 'de'];
    const priorityLangs = dbLanguages.filter(lang => priorityCodes.includes(lang.code)).sort((a, b) => priorityCodes.indexOf(a.code) - priorityCodes.indexOf(b.code));
    const otherLangs = dbLanguages.filter(lang => !priorityCodes.includes(lang.code)).sort((a, b) => t(`lang_${a.code}`).localeCompare(t(`lang_${b.code}`), i18n.language));
    return [...priorityLangs, ...otherLangs].map(lang => ({ value: lang.code, label: t(`lang_${lang.code}`) }));
  }, [dbLanguages, i18n.language, t]);

  const targetLanguageOptions = useMemo(() => languageOptions.filter(opt => opt.value !== order.sourceLang), [languageOptions, order.sourceLang]);
  const sourceLanguageOptions = useMemo(() => languageOptions.filter(opt => !order.targetLangs.includes(opt.value)), [languageOptions, order.targetLangs]);

  useEffect(() => {
    const siteLanguage = i18n.language.split('-')[0];
    if (languageOptions.length > 0 && order.sourceLang !== siteLanguage && languageOptions.some(opt => opt.value === siteLanguage)) {
      setOrder(p => ({ ...p, sourceLang: siteLanguage }));
    }
  }, [i18n.language, languageOptions, order.sourceLang]);

  useEffect(() => { if (order.wordCount > 0 && !user) setIsAuthModalOpen(true); }, [order.wordCount, user]);

  const performPriceCalculation = useCallback(async (codeToApply) => {
    if (!pricingConfig || !user || order.wordCount === 0 || order.targetLangs.length === 0) {
      setTotalPrice(0);
      setBasePriceTL(0);
      return; // Erken çıkış
    }

    // codeToApply boş değilse (yani butona basıldıysa) yükleniyor durumunu başlat
    if (codeToApply) {
        setCouponLoading(true);
    }
    setCouponError('');

    const orderDetailsForBackend = {
        sourceLang: order.sourceLang,
        targetLangs: order.targetLangs,
        wordCount: order.wordCount,
        translatorType: order.translatorType,
        expertise: order.expertise,
        deliverySpeed: order.deliverySpeed,
        requiresDtp: order.extraServices.dtp,
        requiresShipping: order.extraServices.kargo,
        requiresNotary: order.extraServices.notary,
		fileCount: order.files.length,
    };

    try {
        const response = await fetch(`${API_URL}/api/pricing/calculate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ 
                orderDetails: orderDetailsForBackend,
                couponCode: codeToApply // Parametre olarak gelen kodu kullan
            })
        });
        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.messageKey || t('error_price_calculation'));
        }
        
        const originalPriceInTL = data.originalPrice;
        const finalPriceInTL = data.finalPrice;

        setBasePriceTL(originalPriceInTL);
        setAppliedCoupon(data.discountApplied);

        const targetRate = rates[selectedCurrency] || 1;
        setTotalPrice(finalPriceInTL / targetRate);

    } catch (err) {
    console.error("Fiyat hesaplama hatası:", err);

    // Sadece kupon denemesi sırasında hata oluştuysa göster
    if (codeToApply) {
        // Gelen hata mesajı, çeviri anahtarı ile aynı mı diye kontrol et.
        // Eğer aynıysa, bu çevirinin henüz yüklenmediği anlamına gelir.
        // Bu durumda, i18next'in çevirmesini beklemeden, backend'in gönderdiği
        // ham mesajın kendisini (yani anahtarı) göstermek yerine,
        // daha genel ve her zaman çevrilmiş olacak bir hata mesajı gösterelim.
        const errorMessageKey = err.message;
        const translatedError = t(errorMessageKey);

        if (translatedError === errorMessageKey) {
            // Çeviri bulunamadı, genel bir hata göster
            setCouponError(t('error_price_calculation'));
        } else {
            // Çeviri başarılı, göster
            setCouponError(translatedError);
        }
    }
    
    // Kupon başarısız olursa, indirimsiz fiyatı tekrar hesapla
    setAppliedCoupon(null); 
    const targetRate = rates[selectedCurrency] || 1;
    // basePriceTL sıfır olabilir, bu durumda totalPrice da sıfır olur.
    setTotalPrice(basePriceTL > 0 ? (basePriceTL / targetRate) : 0);

} finally {
        if (codeToApply) {
            setCouponLoading(false);
        }
    }
}, [order, selectedCurrency, user, pricingConfig, token, t, rates, basePriceTL]); // basePriceTL'yi ekledik

 useEffect(() => {
    // Sipariş detayları değiştiğinde, kuponu sıfırla ve indirimsiz fiyatı hesapla.
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
    performPriceCalculation(''); // Kupon alanı boş olacak şekilde çağır
}, [order, selectedCurrency]); // Sadece sipariş ve para birimi değiştiğinde çalışsın

  const calculateDeliveryDate = (currentOrder) => {
    if (!user || currentOrder.wordCount === 0 || currentOrder.targetLangs.length === 0) return null;
    let deliveryDays = 0, deliveryHours = 0;
    const wc = currentOrder.wordCount;
    if (wc < 2000) { const baseHours = (wc / 400) * 9; deliveryHours = (currentOrder.deliverySpeed === 'fast') ? baseHours / 2 : baseHours; } 
    else { if (wc <= 4000) { deliveryDays = 2; } else if (wc <= 6000) { deliveryDays = 3; } else if (wc <= 10000) { deliveryDays = 4; } else if (wc <= 15000) { deliveryDays = 6; } else if (wc <= 20000) { deliveryDays = 8; } else if (wc <= 25000) { deliveryDays = 12; } else if (wc <= 35000) { deliveryDays = 16; } else if (wc <= 45000) { deliveryDays = 20; } else if (wc <= 55000) { deliveryDays = 24; } else { const baseDays = 24; const additionalWords = wc - 55000; const additionalBlocks = Math.ceil(additionalWords / 10000); deliveryDays = baseDays + (additionalBlocks * 4); } if (currentOrder.deliverySpeed === 'fast') { deliveryDays = deliveryDays / 2; } }
    const deliveryDate = new Date();
    if (deliveryDays > 0) { deliveryDate.setDate(deliveryDate.getDate() + Math.max(1, Math.ceil(deliveryDays))); } else { deliveryDate.setHours(deliveryDate.getHours() + Math.max(1, Math.ceil(deliveryHours))); }
    return deliveryDate;
  };

  const deliveryDateObject = useMemo(() => calculateDeliveryDate(order), [user, order]);
  const dateLocales = { tr: tr, en: enUS };

  const deliveryDateString = useMemo(() => {
    if (!deliveryDateObject) return null;
    const localeCode = i18n.language.split('-')[0];
    const formatString = localeCode === 'en' ? "MMMM d, yyyy, h:mm a" : "d MMMM yyyy, HH:mm";
    return formatInTimeZone(deliveryDateObject, selectedTimezone, formatString, { locale: dateLocales[localeCode] || enUS });
  }, [deliveryDateObject, i18n.language, selectedTimezone, dateLocales]);
  
  useEffect(() => {
    if (isFileTypeModalOpen || isTimezoneOpen || isAuthModalOpen || isPaymentModalOpen) { document.body.style.overflow = 'hidden'; } 
    else { document.body.style.overflow = 'unset'; }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isFileTypeModalOpen, isTimezoneOpen, isAuthModalOpen, isPaymentModalOpen]);
  
  const handleAuthSuccess = () => setIsAuthModalOpen(false);
  const isStep2Complete = order.wordCount > 0 && order.targetLangs.length > 0;
  
  const handleStepClick = (step) => {
    if (!isStep2Complete && step > 2) {
      if (step2Ref.current) {
        step2Ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        step2Ref.current.classList.add('highlight-shake');
        setTimeout(() => { if (step2Ref.current) step2Ref.current.classList.remove('highlight-shake'); }, 600);
      }
      alert(t('alert_upload_first'));
    } else {
      setActiveStep(step);
    }
  };

  const handleSourceLangChange = (opt) => {
    setOrder(p => ({ ...p, sourceLang: opt.value, targetLangs: p.targetLangs.filter(lang => lang !== opt.value) }));
    setCouponCode(''); setAppliedCoupon(null);
  };
  const handleTargetLangsChange = (opts) => {
    setOrder(p => ({ ...p, targetLangs: opts ? opts.map(o => o.value) : [] }));
    setCouponCode(''); setAppliedCoupon(null);
  };
  const handleTextChange = (e) => { 
    const words = e.target.value.trim().split(/\s+/).filter(Boolean).length; 
    setOrder(p => ({ ...p, text: e.target.value, wordCount: words, files: [] })); 
    setCouponCode(''); setAppliedCoupon(null);
  };
  const handleFileChange = async (e) => {
    const filesToUpload = Array.from(e.target.files);
    if (filesToUpload.length === 0) return;
    setIsCounting(true);
    let newFilesData = [];
    let newWordCount = 0;
    for (const file of filesToUpload) {
      const formData = new FormData();
      formData.append('document', file);
      try {
        const response = await fetch(`${API_URL}/api/word-count`, { method: 'POST', body: formData });
        if (!response.ok) { let errorText = `Dosya işlenemedi: ${file.name}`; try { const errorData = await response.json(); errorText = errorData.error || errorText; } catch(err){ /* ignore */ } throw new Error(errorText); }
        const result = await response.json();
        newFilesData.push({ file: file, wordCount: result.wordCount, originalFilename: result.originalFilename, storedFilename: result.storedFilename });
        newWordCount += result.wordCount;
      } catch (error) {
        alert(error.message);
        setIsCounting(false);
        return;
      }
    }
    setOrder(p => ({ ...p, files: [...p.files, ...newFilesData], wordCount: p.wordCount + newWordCount, text: '' }));
    setIsCounting(false);
    setCouponCode(''); setAppliedCoupon(null);
  };
  const removeFile = (fileIndexToRemove) => {
    const fileToRemove = order.files[fileIndexToRemove];
    if (!fileToRemove) return;
    setOrder(p => ({ ...p, files: p.files.filter((_, index) => index !== fileIndexToRemove), wordCount: Math.max(0, p.wordCount - fileToRemove.wordCount) }));
    setCouponCode(''); setAppliedCoupon(null);
  };
  const handleTranslatorTypeChange = (type) => { setOrder(p => ({ ...p, translatorType: type })); setCouponCode(''); setAppliedCoupon(null); };
  const handleExpertiseChange = (expertise) => { setOrder(p => ({ ...p, expertise: expertise })); setCouponCode(''); setAppliedCoupon(null); };
  const handleDeliverySpeedChange = (speed) => { setOrder(p => ({ ...p, deliverySpeed: speed })); setCouponCode(''); setAppliedCoupon(null); };
  const handleExtraServiceChange = (e) => { const { name, checked } = e.target; setOrder(p => ({ ...p, extraServices: { ...p.extraServices, [name]: checked } })); setCouponCode(''); setAppliedCoupon(null); };
  const getGmtOffsetString = (timezone) => { try { return `GMT${formatInTimeZone(new Date(), timezone, 'xxx')}`; } catch (e) { return ''; } };
  
  const handleApplyCoupon = () => {
    if (!couponCode) return; // Boşsa bir şey yapma
    performPriceCalculation(couponCode); // State'deki güncel kupon kodu ile çağır
};

  const handleInitiatePayment = async () => {
    if (!isStep2Complete || !user) { alert(t('alert_fill_all_fields')); return; }
    if (totalPrice <= 0) { alert(t('alert_invalid_price')); return; }
    setIsSubmitting(true);
    setSubmitMessage({ type: '', text: '' });
    try {
      const response = await fetch(`${API_URL}/api/payments/create-payment-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ 
    amount: totalPrice, // TUTARI OLDUĞU GİBİ GÖNDER (ÖR: 34862.06)
    currency: selectedCurrency 
})
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Ödeme başlatılamadı.');
      setClientSecret(data.clientSecret);
      setIsPaymentModalOpen(true);
    } catch (err) {
      setSubmitMessage({ type: 'error', text: err.message });
      setIsSubmitting(false);
    }
  };

  const handlePaymentSuccess = async () => {
    setIsPaymentModalOpen(false);
    setSubmitMessage({ type: 'info', text: t('payment_successful_creating_order') });
    try {
      const finalDeliveryDate = calculateDeliveryDate(order);
      const orderData = { 
        sourceLang: order.sourceLang, targetLangs: order.targetLangs, expertise: order.expertise, 
        translatorType: order.translatorType,
        wordCount: order.wordCount, 
        deliverySpeed: order.deliverySpeed, 
        requiresDtp: order.extraServices.dtp, requiresShipping: order.extraServices.kargo, requiresNotary: order.extraServices.notary,
fileCount: order.files.length,		
        price: totalPrice,
        priceInTL: basePriceTL,
        discountedPriceInTL: appliedCoupon ? (appliedCoupon.value_type === 'percent' ? basePriceTL * (1 - (appliedCoupon.value / 100)) : basePriceTL - appliedCoupon.value) : basePriceTL,
        couponCodeUsed: appliedCoupon ? appliedCoupon.code : null,
        discountIdUsed: appliedCoupon ? appliedCoupon.id : null,
        deadline: finalDeliveryDate ? finalDeliveryDate.toISOString() : null,
        timezone: selectedTimezone,
        uploadedFiles: order.files.map(f => ({ originalFilename: f.originalFilename, storedFilename: f.storedFilename, wordCount: f.wordCount }))
      };
      
      const response = await fetch(`${API_URL}/api/jobs/create`, { 
        method: 'POST', 
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, 
        body: JSON.stringify(orderData) 
      });
      
      if (!response.ok) { 
        const data = await response.json();
        throw new Error(t(data.messageKey) || t('error_order_submission')); 
      }
      
      if (appliedCoupon && appliedCoupon.id) {
          fetch(`${API_URL}/api/pricing/admin/discounts/${appliedCoupon.id}/increment-usage`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}` }
          }).catch(err => console.error("Failed to increment coupon usage:", err));
      }

     setModalMessage(t('success_order_created_redirecting'));
      setIsSuccessModalOpen(true);

     setTimeout(() => {
        setIsSuccessModalOpen(false); 
        navigate('/account/orders');
      }, 3000);

    } catch (err) {
      setSubmitMessage({ type: 'error', text: err.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedSourceLang = languageOptions.find(opt => opt.value === order.sourceLang);
  const selectedTargetLangs = languageOptions.filter(opt => order.targetLangs.includes(opt.value));
  const anyExtraServiceSelected = Object.values(order.extraServices).some(isSelected => isSelected);
  const basePriceInSelectedCurrency = basePriceTL / (rates[selectedCurrency] || 1);

  return (
    <div className="order-container">
      <div className="form-column">
        {/* Adım 1 */}
        <div className="step-row" onClick={() => handleStepClick(1)}>
            <div className="stepper-column">
                <div className={`step-indicator ${activeStep === 1 ? 'active' : isStep2Complete ? 'completed' : ''}`}>1</div>
                <div className="step-line"></div>
            </div>
            <div className="step-content">
                <div className="step-card">
                    <h3><i className="fas fa-language"></i> {t('step1_title')}</h3>
                    <div className="language-selectors">
                        <div className="form-group">
                            <label>{t('source_language')}</label>
                            <Select value={selectedSourceLang} onChange={handleSourceLangChange} options={sourceLanguageOptions} styles={customSelectStyles} />
                        </div>
                        <div className="form-group">
                            <label>{t('target_language')}</label>
                            <Select isMulti value={selectedTargetLangs} onChange={handleTargetLangsChange} options={targetLanguageOptions} placeholder={t('select_target_languages_placeholder')} styles={customSelectStyles} />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {/* Adım 2 */}
        <div className="step-row" ref={step2Ref} onClick={() => handleStepClick(2)}>
            <div className="stepper-column">
                <div className={`step-indicator ${activeStep === 2 ? 'active' : isStep2Complete ? 'completed' : ''}`}>2</div>
                <div className="step-line"></div>
            </div>
            <div className="step-content">
                <div className="step-card">
                    <div className="step-card-header">
                        <h3><i className="fas fa-file-arrow-up"></i> {t('step2_title')}</h3>
                        <div className="header-meta">
                            <span className="privacy-notice"><i className="fas fa-lock"></i> {t('privacy_notice')}</span>
                            <button className="help-icon" onClick={() => setIsFileTypeModalOpen(true)}><i className="fas fa-question-circle"></i></button>
                        </div>
                    </div>
                    <div className="upload-tabs">
                        <button className={`tab-btn ${inputMode === 'file' ? 'active' : ''}`} onClick={() => setInputMode('file')}><i className="fas fa-upload"></i> {t('upload_file')}</button>
                        <button className={`tab-btn ${inputMode === 'text' ? 'active' : ''}`} onClick={() => setInputMode('text')}><i className="fas fa-align-left"></i> {t('enter_text')}</button>
                    </div>
                    {inputMode === 'file' ? (
                        <div className="file-upload-content">
                            <label htmlFor="file-upload" className={`upload-btn-styled ${isCounting ? 'disabled' : ''}`}>
                                {isCounting ? (<><i className="fas fa-spinner fa-spin"></i> {t('counting_words')}</>) : (<><i className="fas fa-file-import"></i> {t('select_your_document')}</>)}
                            </label>
                            <input id="file-upload" type="file" onChange={handleFileChange} multiple disabled={isCounting} />
                            <p className="upload-info-text">{t('upload_info')}</p>
                            {order.files.length > 0 && (
                                <div className="file-list">
                                    {order.files.map((fileData, index) => (
                                        <div key={index} className="file-item">
                                            <span><i className="fas fa-file"></i> {fileData.file.name}</span>
                                            <button onClick={() => removeFile(index)} className="remove-file-btn"><i className="fas fa-times"></i></button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <textarea className="text-input-area" placeholder={t('text_area_placeholder')} value={order.text} onChange={handleTextChange} />
                    )}
                </div>
            </div>
        </div>
        
        {/* Adım 3'ten 6'ya kadar olan kısım değişmedi */}
        <div className={`step-row ${!isStep2Complete ? 'disabled' : ''}`} onClick={() => handleStepClick(3)}>
            <div className="stepper-column"><div className={`step-indicator ${activeStep === 3 ? 'active' : ''}`}>3</div><div className="step-line"></div></div>
            <div className="step-content"><div className="step-card"><h3><i className="fas fa-user-tie"></i> {t('step3_title')}</h3><div className="choice-buttons"><button className={order.translatorType === 'sworn_translator' ? 'active' : ''} onClick={() => handleTranslatorTypeChange('sworn_translator')}>{t('sworn_translator')}</button><button className={order.translatorType === 'professional_translator' ? 'active' : ''} onClick={() => handleTranslatorTypeChange('professional_translator')}>{t('professional_translator')}</button></div></div></div>
        </div>
        <div className={`step-row ${!isStep2Complete ? 'disabled' : ''}`} onClick={() => handleStepClick(4)}>
            <div className="stepper-column"><div className={`step-indicator ${activeStep === 4 ? 'active' : ''}`}>4</div><div className="step-line"></div></div>
            <div className="step-content"><div className="step-card"><h3><i className="fas fa-briefcase"></i> {t('step4_title')}</h3><div className="expertise-buttons"><button className={order.expertise === 'general' ? 'active' : ''} onClick={() => handleExpertiseChange('general')}>{t('general')}</button><button className={order.expertise === 'legal' ? 'active' : ''} onClick={() => handleExpertiseChange('legal')}>{t('legal')}</button><button className={order.expertise === 'medical' ? 'active' : ''} onClick={() => handleExpertiseChange('medical')}>{t('medical')}</button><button className={order.expertise === 'technical' ? 'active' : ''} onClick={() => handleExpertiseChange('technical')}>{t('technical')}</button><button className={order.expertise === 'academic' ? 'active' : ''} onClick={() => handleExpertiseChange('academic')}>{t('academic')}</button></div></div></div>
        </div>
        <div className={`step-row ${!isStep2Complete ? 'disabled' : ''}`} onClick={() => handleStepClick(5)}>
            <div className="stepper-column"><div className={`step-indicator ${activeStep === 5 ? 'active' : ''}`}>5</div><div className="step-line"></div></div>
            <div className="step-content"><div className="step-card"><h3><i className="fas fa-bolt"></i> {t('step5_title')}</h3><div className="choice-buttons"><button className={order.deliverySpeed === 'fast' ? 'active' : ''} onClick={() => handleDeliverySpeedChange('fast')}>{t('fast_delivery')}</button><button className={order.deliverySpeed === 'standard' ? 'active' : ''} onClick={() => handleDeliverySpeedChange('standard')}>{t('standard_delivery')}</button></div></div></div>
        </div>
        <div className={`step-row ${!isStep2Complete ? 'disabled' : ''}`} onClick={() => handleStepClick(6)}>
            <div className="stepper-column"><div className={`step-indicator ${activeStep === 6 ? 'active' : ''}`}>6</div></div>
            <div className="step-content"><div className="step-card"><h3><i className="fas fa-plus-circle"></i> {t('step6_title')}</h3><div className="checkbox-group"><label className="checkbox-label with-tooltip"><input type="checkbox" name="dtp" checked={order.extraServices.dtp} onChange={handleExtraServiceChange} disabled={!isStep2Complete}/>{t('dtp_service')}<span className={`tooltip-trigger ${!isStep2Complete ? 'disabled' : ''}`}>?</span>
			<span className="tooltip-text tooltip-large">{t('tooltip_dtp_service')}</span></label><label className="checkbox-label"><input type="checkbox" name="kargo" checked={order.extraServices.kargo} onChange={handleExtraServiceChange} disabled={!isStep2Complete}/>{t('shipping_service')}</label><label className="checkbox-label"><input type="checkbox" name="notary" checked={order.extraServices.notary} onChange={handleExtraServiceChange} disabled={!isStep2Complete}/>{t('notary_service')}</label></div></div></div>
        </div>
      </div>
      

      <div className="summary-card">
        <h3>{t('summary_title')}</h3>
        <div className="summary-item"><span><i className="fas fa-file-word"></i> {t('word_count')}:</span><strong>{order.wordCount}</strong></div>
        <div className="summary-item"><span><i className="fas fa-globe"></i> {t('translation_languages')}:</span><strong>{selectedTargetLangs.map(lang => lang.label).join(', ')}</strong></div>
        <div className="summary-item"><span><i className="fas fa-clock"></i> {t('estimated_delivery')}:</span><strong>{deliveryDateString || '-'}</strong></div>
        {deliveryDateString && (<div className="summary-item timezone-item">
		<span><i className="fas fa-globe-americas"></i> {t('local_timezone')}:</span><button className="timezone-button" onClick={() => setIsTimezoneOpen(true)}>{getGmtOffsetString(selectedTimezone)}<span className="tooltip-text tooltip-small">{t('tooltip_change_timezone')}</span></button></div>)}
        <hr/>
        <div className="summary-item"><span><i className="fas fa-user-tie"></i> {t('translator_type')}:</span><strong>{t(order.translatorType)}</strong></div>
        <div className="summary-item"><span><i className="fas fa-briefcase"></i> {t('expertise_area')}:</span><strong>{t(order.expertise)}</strong></div>
        <div className="summary-item"><span><i className="fas fa-bolt"></i> {t('delivery_speed')}:</span><strong>{t(`${order.deliverySpeed}_delivery`)}</strong></div>
        {anyExtraServiceSelected && (<><hr/><div className="summary-subtitle">{t('extra_services')}</div><div className="extra-services-list">{order.extraServices.dtp && <div className="extra-service-item">- {t('dtp_service')}</div>}{order.extraServices.kargo && <div className="extra-service-item">- {t('shipping_service')}</div>}{order.extraServices.notary && <div className="extra-service-item">- {t('notary_service')}</div>}</div></>)}
        
        {isStep2Complete && (
            <><hr/><div className="coupon-section"><label htmlFor="coupon-input">{t('coupon_code')}</label><div className="coupon-input-group"><input id="coupon-input" type="text" placeholder={t('enter_coupon_code')} value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())} disabled={couponLoading || !!appliedCoupon} /><button onClick={handleApplyCoupon} disabled={!couponCode || couponLoading || !!appliedCoupon}>{couponLoading ? <i className="fas fa-spinner fa-spin"></i> : t('apply')}</button></div>{couponError && <p className="coupon-error">{couponError}</p>}</div></>
        )}

        {appliedCoupon && (
            <div className="price-breakdown">
                <div className="summary-item original-price"><span>{t('subtotal')}</span><strong><s>{basePriceInSelectedCurrency.toLocaleString(i18n.language, { style: 'currency', currency: selectedCurrency })}</s></strong></div>
                <div className="summary-item discount-applied"><span><i className="fas fa-tag"></i> {t('discount')} ({appliedCoupon.code || appliedCoupon.description})
				<button 
    className="remove-coupon-btn" 
    onClick={() => {
        setCouponCode('');
        setAppliedCoupon(null);
        setCouponError('');
        performPriceCalculation(''); // <-- DÜZELTME BURADA: Fiyatı yeniden hesapla
    }}
>
    {t('remove')}
</button>
				</span><strong>- {(basePriceInSelectedCurrency - totalPrice).toLocaleString(i18n.language, { style: 'currency', currency: selectedCurrency })}</strong></div>
            </div>
        )}

        <div className="total-price">
            <span>{t('total_amount')}</span>
            <h2>{totalPrice.toLocaleString(i18n.language, { style: 'currency', currency: selectedCurrency, minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h2>
        </div>

        <button className="cta-button" onClick={user ? handleInitiatePayment : () => setIsAuthModalOpen(true)} disabled={isSubmitting || !isStep2Complete || totalPrice <= 0}>
          {isSubmitting ? (<><i className="fas fa-spinner fa-spin"></i> {t('submitting_order')}</>) : (user ? t('complete_order_securely') : t('continue_order'))}
        </button>
       
      </div>

      <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} onSuccess={handleAuthSuccess} />
      <FileTypeModal isOpen={isFileTypeModalOpen} onClose={() => setIsFileTypeModalOpen(false)} />
      <TimezoneSwitcher isOpen={isTimezoneOpen} onClose={() => setIsTimezoneOpen(false)} timezones={timezones} selectedTimezone={selectedTimezone} onTimezoneChange={setSelectedTimezone} />

      {isPaymentModalOpen && clientSecret && (
        <Elements stripe={stripePromise} options={{ clientSecret }}>
            <PaymentModal isOpen={isPaymentModalOpen} onClose={() => { setIsPaymentModalOpen(false); setIsSubmitting(false); }} onPaymentSuccess={handlePaymentSuccess} />
        </Elements>
      )}
    <SuccessModal
        isOpen={isSuccessModalOpen}
        onClose={() => {
            setIsSuccessModalOpen(false);
            navigate('/account/orders'); 
        }}
       title={t('order_successful_title')}
        
        message={modalMessage}
      />
    </div>
  );
};

export default OrderPage;